import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { MERCH, ARTIST } from "../mock";

const CATS = ["Tout", "Musique", "Bundle"];

export default function Merch() {
  const [filter, setFilter] = useState("Tout");

  const filtered = useMemo(
    () => (filter === "Tout" ? MERCH : MERCH.filter((m) => m.category === filter)),
    [filter]
  );

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 lg:py-16" data-testid="merch-page">
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-10">
        <Link to="/" className="hover:text-white" data-testid="breadcrumb-home">Accueil</Link>
        <span>/</span>
        <span className="text-white">Merchandising</span>
      </div>

      <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
        <div className="md:col-span-8 reveal-up">
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-500 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
            04 · Boutique
          </div>
          <h1 className="title-reveal font-display uppercase text-[14vw] md:text-[9vw] leading-[0.85] tracking-tight">
            Merch<br />
            <span className="text-blue-600">Vince Rodi</span>
          </h1>
          <div className="mt-6 flex items-center gap-3">
            <span className="accent-line-red" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">Disponible en ligne &amp; après chaque concert</span>
          </div>
        </div>
        <div className="md:col-span-4 reveal-up" style={{ animationDelay: "120ms" }}>
          <p className="text-neutral-300 text-sm leading-relaxed">
            Retrouvez ces articles exclusifs <span className="text-red-500 font-medium">disponibles en ligne</span> et après chaque concert. CD dédicacés et bundles fan.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-10" data-testid="merch-filters">
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            data-testid={`filter-${c.toLowerCase()}`}
            className={`text-xs uppercase tracking-[0.2em] px-4 py-2 border transition-all ${
              filter === c
                ? "bg-white text-black border-white"
                : "border-white/20 text-neutral-400 hover:border-white hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="merch-grid">
        {filtered.map((p) => (
          <article
            key={p.id}
            data-testid={`merch-item-${p.id}`}
            className="group border border-white/10 hover:border-red-500/40 transition-all bg-neutral-950"
          >
            <div className="aspect-[4/5] overflow-hidden bg-black relative">
              <img
                src={p.image}
                alt={p.name}
                className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-700"
              />
              <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] bg-black/70 backdrop-blur px-2 py-1 text-neutral-200 border border-white/10">
                {p.category}
              </div>
              {p.category === "Bundle" && (
                <div
                  data-testid="bundle-limited-badge"
                  className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.25em] bg-red-500 text-white px-2.5 py-1 border border-red-500 shadow-lg shadow-red-500/30"
                >
                  Édition limitée
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-display text-2xl uppercase tracking-tight leading-tight">{p.name}</h3>
                <div className="font-display text-2xl whitespace-nowrap text-red-500">{p.price} €</div>
              </div>
              <p className="text-sm text-neutral-400 leading-relaxed mb-5">{p.description}</p>
              <div className="flex items-center justify-between pt-3 border-t border-white/10">
                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                  <MapPin size={11} className="text-blue-500" />
                  En ligne &amp; après concert
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Info bar */}
      <div className="mt-16 border border-white/15 p-6 md:p-8 grid md:grid-cols-3 gap-6 text-sm" data-testid="merch-info-bar">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 mb-2">Disponibilité</div>
          <div className="text-neutral-200">Disponible en ligne et après chaque concert.</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-500 mb-2">Paiement</div>
          <div className="text-neutral-200">Espèces · Chapeau électronique (QR code sur place)</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 mb-2">Question · Réservation</div>
          <a
            href={`mailto:${ARTIST.email}`}
            data-testid="merch-contact-mailto"
            className="inline-flex items-center gap-2 text-neutral-200 hover:text-white underline underline-offset-4"
          >
            {ARTIST.email} <ArrowUpRight size={14} />
          </a>
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link
          to="/tournee"
          className="inline-flex items-center gap-2 border border-white/25 hover:border-white hover:bg-white hover:text-black transition-all px-5 py-3 text-xs uppercase tracking-[0.2em]"
          data-testid="merch-to-tour"
        >
          Voir les dates de tournée <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}
